import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { furnitureData } from '../data/mock';

export const Furniture = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Furniture' },
    { id: 'sofas', label: 'Sofas' },
    { id: 'sectionals', label: 'Sectionals' },
    { id: 'chairs', label: 'Chairs' },
    { id: 'dining-chairs', label: 'Dining Chairs' },
    { id: 'ottomans', label: 'Ottomans' },
    { id: 'beds', label: 'Beds' }
  ];

  const filteredFurniture = selectedCategory === 'all'
    ? furnitureData
    : furnitureData.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1687180497278-ca4d736ecc99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmdXJuaXR1cmUlMjBzaG93cm9vbXxlbnwwfHx8fDE3NzA4MTk3MzZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Furniture Collection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-800 mb-4">
              Our Furniture
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Exquisite pieces crafted with care and expertise
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <section className="bg-rose-50 border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-rose-400 text-white shadow-sm'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Furniture Grid */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredFurniture.map((item) => (
            <div
              key={item.id}
              className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all"
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="text-sm text-rose-400 font-medium mb-2">
                  {item.categoryLabel}
                </div>
                <h3 className="text-xl font-serif font-semibold text-gray-800 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {item.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{item.style}</span>
                  <Link 
                    to="/contact" 
                    className="text-rose-400 font-medium hover:text-rose-500 transition-colors"
                  >
                    Inquire →
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info Section */}
      <section className="bg-rose-50 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4">
              Don't See What You're Looking For?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We specialize in custom furniture design and can bring any vision to life. 
              Whether you need a specific style, size, or finish, our expert craftsmen can create 
              the perfect piece for your space.
            </p>
            <a
              href="/contact"
              className="inline-block bg-rose-400 text-white px-8 py-4 rounded-lg font-semibold hover:bg-rose-500 transition-colors shadow-sm"
            >
              Request Custom Design
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-serif font-bold text-gray-800 mb-4">
            Our Process
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From consultation to completion, we ensure every detail is perfect
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-serif font-bold text-rose-400">1</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Consultation</h3>
            <p className="text-gray-600">
              Discuss your vision, needs, and preferences with our experts
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-serif font-bold text-rose-400">2</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Design</h3>
            <p className="text-gray-600">
              Create detailed plans and select materials for your project
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-serif font-bold text-rose-400">3</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Crafting</h3>
            <p className="text-gray-600">
              Expert craftsmen bring your piece to life with precision
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-serif font-bold text-rose-400">4</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Delivery</h3>
            <p className="text-gray-600">
              Careful delivery and installation of your finished furniture
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
